import { useLayoutEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { kindWords } from '@/mocks/editorial';
import useEditorialMotion from '@/hooks/useEditorialMotion';

const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function Quote({ item }: { item: (typeof kindWords)[number] }) {
  return (
    <>
      <blockquote className="font-light tracking-[-0.035em] leading-[1.08] text-[28px] sm:text-[40px] lg:text-[56px]">
        “{item.quote}”
      </blockquote>
      <figcaption className="mt-8 md:mt-12 text-[13px] text-black/60">
        — {item.attribution}, <span className="ff-serif italic">{item.context}</span>
      </figcaption>
    </>
  );
}

export default function KindWords() {
  const ref = useRef<HTMLElement>(null);
  const figRef = useRef<HTMLElement>(null);
  const busy = useRef(false);
  const entering = useRef(false);
  const [i, setI] = useState(0);
  useEditorialMotion(ref);

  const step = (dir: number) => setI((v) => (v + dir + kindWords.length) % kindWords.length);

  const go = (dir: number) => {
    if (busy.current) return;
    const el = figRef.current;
    if (!el || reduced()) {
      step(dir);
      return;
    }
    busy.current = true;
    gsap.to(el, {
      y: -10, opacity: 0, duration: 0.17, ease: 'power2.in',
      onComplete: () => {
        entering.current = true;
        step(dir);
      },
    });
  };

  useLayoutEffect(() => {
    if (!entering.current) return;
    entering.current = false;
    const el = figRef.current;
    if (!el) {
      busy.current = false;
      return;
    }
    gsap.fromTo(el, { y: 10, opacity: 0 }, {
      y: 0, opacity: 1, duration: 0.22, ease: 'power2.out', clearProps: 'transform,opacity',
      onComplete: () => { busy.current = false; },
    });
  }, [i]);

  return (
    <section ref={ref} id="kind-words" aria-roledescription="carousel" aria-label="Kind words" className="bg-white px-4 md:px-10 py-20 md:py-36">
      <div className="flex items-baseline justify-between gap-4 mb-12 md:mb-20">
        <p data-m-fade className="text-[11px] md:text-[12px] tracking-wide text-black/50">06 / KIND WORDS</p>
        <p data-m-fade data-m-at="0.1" className="ff-serif italic text-[13px] text-black/40">Sample words — demo content</p>
      </div>
      <div data-m-head className="grid max-w-[1100px]">
        {/* Invisible sizers reserve the tallest quotation so switching never jumps */}
        {kindWords.map((k) => (
          <figure key={k.quote} aria-hidden="true" className="[grid-area:1/1] invisible pointer-events-none"><Quote item={k} /></figure>
        ))}
        <figure ref={figRef} aria-live="polite" className="[grid-area:1/1]">
          <Quote item={kindWords[i]} />
        </figure>
      </div>
      <div className="mt-12 md:mt-16 flex items-center gap-6 text-[13px]">
        <button type="button" onClick={() => go(-1)} aria-label="Previous quotation" className="group cursor-pointer whitespace-nowrap hover:text-black/50 transition-colors duration-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4">
          <span className="inline-block transition-transform duration-[240ms] group-hover:-translate-x-1">←</span> Prev
        </button>
        <span className="tabular-nums text-black/40">{String(i + 1).padStart(2, '0')} / {String(kindWords.length).padStart(2, '0')}</span>
        <button type="button" onClick={() => go(1)} aria-label="Next quotation" className="group cursor-pointer whitespace-nowrap hover:text-black/50 transition-colors duration-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4">
          Next <span className="inline-block transition-transform duration-[240ms] group-hover:translate-x-1">→</span>
        </button>
      </div>
    </section>
  );
}